import type { ContactInput } from '../utils/validation.ts';

/**
 * Per-device client memory: which bookings were made here (id + cancel token)
 * and the last contact details, so returning clients book in two taps.
 */
export interface BookingRef {
  id: string;
  cancel_token: string;
}

const refsKey = (slug: string) => `termine:my:${slug}`;
const CONTACT_KEY = 'termine:contact';

export function getBookingRefs(slug: string): BookingRef[] {
  return safeRead<BookingRef[]>(refsKey(slug)) ?? [];
}

export function addBookingRef(slug: string, ref: BookingRef): void {
  safeWrite(refsKey(slug), [...getBookingRefs(slug).filter((r) => r.id !== ref.id), ref]);
}

export function getSavedContact(): ContactInput | null {
  return safeRead<ContactInput>(CONTACT_KEY);
}

export function saveContact(contact: ContactInput): void {
  safeWrite(CONTACT_KEY, contact);
}

export function forgetContact(): void {
  try {
    localStorage.removeItem(CONTACT_KEY);
  } catch {
    /* storage blocked */
  }
}

/**
 * Links in e-mails carry the booking reference in the URL fragment
 * (/s/<slug>/me#b=<id>&t=<token>): fragments never reach servers or Referer
 * headers. Import it on this device and strip it from the address bar.
 */
export function importBookingRefFromHash(slug: string): BookingRef | null {
  const params = new URLSearchParams(location.hash.slice(1));
  const id = params.get('b');
  const token = params.get('t');
  if (!id || !token || !UUID_RE.test(id) || !UUID_RE.test(token)) return null;
  const ref = { id, cancel_token: token };
  addBookingRef(slug, ref);
  history.replaceState(null, '', location.pathname);
  return ref;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function safeRead<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: the booking itself still succeeded */
  }
}
